import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import { TemplateType, getTemplateTypeEntry } from '../template-type';
import { PlaceholderKey } from './placeholder-keys';
import type { PlaceholderData, PlaceholderKind } from './types';

const MULTI_MATCH_TEMPLATE_TYPES: ReadonlySet<TemplateType> = new Set([
  TemplateType.MULTIPLE_SCHEDULE,
  TemplateType.MULTIPLE_RESULTS,
]);

/**
 * Returns true when the active template type renders several matches at once
 * and the given key resolves against per-match data. Used by the inspector to
 * show the `Match #` field and red-highlight a missing value.
 *
 * Keys that come from per-match data are anything that the resolver routes
 * through a match slot. In practice that's every SCHEDULE/RESULTS key except
 * pure tournament/championship metadata.
 */
const MATCH_SCOPED_KEYS: ReadonlySet<string> = new Set<string>([
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.SCORE,
  PlaceholderKey.SCORE_TEAM,
  PlaceholderKey.MATCH_DATE,
  PlaceholderKey.MATCH_TIME,
  PlaceholderKey.MATCH_VENUE,
  PlaceholderKey.ROUND_NAME,
]);

export function isMultiMatchTemplateType(type: TemplateType | undefined): boolean {
  return type !== undefined && MULTI_MATCH_TEMPLATE_TYPES.has(type);
}

export function needsMatchNumber(key: string | undefined, type: TemplateType | undefined): boolean {
  if (!isMultiMatchTemplateType(type)) return false;
  if (!key) return false;
  return MATCH_SCOPED_KEYS.has(key);
}

type MissingFieldsFn = (data: PlaceholderData, templateType?: TemplateType) => readonly string[];

/** What a template renders, as far as a key's meaning is concerned. */
export type DescriptionScope = 'match' | 'multiMatch' | 'table' | 'playerStats';

const DESCRIPTION_SCOPES: Record<TemplateType, DescriptionScope> = {
  [TemplateType.SCHEDULE]: 'match',
  [TemplateType.RESULTS]: 'match',
  [TemplateType.MULTIPLE_SCHEDULE]: 'multiMatch',
  [TemplateType.MULTIPLE_RESULTS]: 'multiMatch',
  [TemplateType.TOURNAMENT_TABLE]: 'table',
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: 'playerStats',
};

type RegistryEntry = {
  /**
   * Inspector label. A descriptor, not a string: the registry is module-level, so
   * the language is not known yet — translate at the call site with `t(label)`.
   * The kit `.claude/skills/media-manager-templates` reads `label.message` (the
   * Russian source) through its own `msg` shim.
   */
  label: MessageDescriptor;
  /**
   * What the key renders to, shown next to the label in the key picker. Text
   * keys only — an image key has no sample, the picker draws an icon instead.
   * A descriptor for the same reason as `label`.
   */
  sample?: MessageDescriptor;
  /**
   * What the key substitutes and when it stays empty, shown in the key picker's
   * details. Written against the resolver, not the label: «Название турнира»
   * actually renders the organizer.
   */
  description: MessageDescriptor;
  /**
   * The description for one kind of template, where the key means something
   * different there — a team is home/away on a match, a row in a table and the
   * player's team in a leaderboard. `description` stays the fallback while the
   * template type is not known yet. Read through `getKeyDescription`.
   */
  descriptionFor?: Partial<Record<DescriptionScope, MessageDescriptor>>;
  /** Two or three real-looking values for the picker's details. Text keys only. */
  examples?: readonly MessageDescriptor[];
  output: 'text' | 'image';
  missingFields: MissingFieldsFn;
};

const isPlayerStats = (t: TemplateType | undefined): boolean =>
  t === TemplateType.TOP_PLAYERS_BY_INDICATORS;

/**
 * Team keys. Normally the team is addressed directly by `teamNumber` (home/away
 * on a match, or a standings row). On a player-statistics template there is no
 * team axis at all: the team is whichever team the ranked player in slot
 * `playerNumber` plays for, so the same key reads a different identity field.
 */
const teamMissing: MissingFieldsFn = (d, t) =>
  isPlayerStats(t)
    ? d.playerNumber
      ? []
      : ['playerNumber']
    : d.teamNumber
      ? []
      : ['teamNumber'];

/**
 * Player keys. Normally a player is (team, index-into-that-team's-roster). On a
 * player-statistics template the rank slot alone identifies the player.
 */
const playerMissing: MissingFieldsFn = (d, t) => {
  if (isPlayerStats(t)) return d.playerNumber ? [] : ['playerNumber'];
  const out: string[] = [];
  if (!d.teamNumber) out.push('teamNumber');
  if (!d.playerNumber) out.push('playerNumber');
  return out;
};

/**
 * Per-championship player stats. Only ever allowed on a player-statistics
 * template, so the rank slot is unconditionally the only identity they read.
 */
const rankMissing: MissingFieldsFn = (d) => (d.playerNumber ? [] : ['playerNumber']);

const none = () => [] as const;

export const PLACEHOLDER_KEY_REGISTRY = {
  [PlaceholderKey.TEAM_NAME]: {
    label: msg`Название команды`,
    sample: msg`Спартак`,
    description: msg`Полное название команды. На матче — хозяев или гостей, в таблице — команды в строке, в статистике — команды игрока на этом месте.`,
    descriptionFor: {
      match: msg`Полное название команды хозяев или гостей — какой из двух, задаёт номер команды.`,
      multiMatch: msg`Полное название команды хозяев или гостей в матче с выбранным номером.`,
      table: msg`Полное название команды, которая стоит в выбранной строке таблицы.`,
      playerStats: msg`Полное название команды, за которую играет игрок на этом месте рейтинга.`,
    },
    examples: [msg`Спартак`, msg`Динамо Москва`, msg`ФК Северный`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.TEAM_SHORT_NAME]: {
    label: msg`Короткое название команды`,
    sample: msg`СПА`,
    description: msg`Сокращённое название команды из её карточки — для тесных мест вроде табло счёта. Какая команда — так же, как у «Названия команды».`,
    descriptionFor: {
      match: msg`Сокращённое название команды хозяев или гостей из её карточки — для тесных мест вроде табло счёта.`,
      multiMatch: msg`Сокращённое название команды хозяев или гостей в матче с выбранным номером — для тесных мест.`,
      table: msg`Сокращённое название команды в выбранной строке таблицы — для узкой колонки.`,
      playerStats: msg`Сокращённое название команды игрока на этом месте рейтинга — для узкой колонки.`,
    },
    examples: [msg`СПА`, msg`ДИН`, msg`СЕВ`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.TEAM_LOGO]: {
    label: msg`Логотип команды`,
    description: msg`Логотип команды из её карточки. Если у команды нет логотипа, место останется пустым.`,
    descriptionFor: {
      match: msg`Логотип команды хозяев или гостей. Если у команды нет логотипа, место останется пустым.`,
      multiMatch: msg`Логотип команды хозяев или гостей в матче с выбранным номером. Нет логотипа — место останется пустым.`,
      table: msg`Логотип команды в выбранной строке таблицы. Нет логотипа — место останется пустым.`,
      playerStats: msg`Логотип команды игрока на этом месте рейтинга. Нет логотипа — место останется пустым.`,
    },
    output: 'image',
    missingFields: teamMissing,
  },
  [PlaceholderKey.SCORE]: {
    label: msg`Счёт`,
    sample: msg`2 : 1`,
    description: msg`Счёт матча целиком: голы хозяев и гостей. Вид задаётся форматом в инспекторе; у несыгранного матча — 0 : 0.`,
    descriptionFor: {
      multiMatch: msg`Счёт матча с выбранным номером целиком: голы хозяев и гостей. Вид задаётся форматом в инспекторе; у несыгранного матча — 0 : 0.`,
    },
    examples: [msg`2 : 1`, msg`0 : 0`, msg`3 – 2`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.SCORE_TEAM]: {
    label: msg`Счёт (команда)`,
    sample: msg`2`,
    description: msg`Голы одной команды — хозяев или гостей. Удобно, когда счёт хозяев и гостей стоит в разных местах картинки.`,
    descriptionFor: {
      multiMatch: msg`Голы хозяев или гостей в матче с выбранным номером. Удобно, когда счёт команд стоит в разных местах строки матча.`,
    },
    examples: [msg`2`, msg`0`, msg`5`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.CHAMPIONSHIP_NAME]: {
    label: msg`Название чемпионата`,
    sample: msg`Кубок города 2026`,
    description: msg`Название турнира, к которому относится картинка, — то, что в админке называется «Турнир».`,
    examples: [msg`Кубок города 2026`, msg`Весенняя лига`, msg`Первенство района`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.TOURNAMENT_NAME]: {
    label: msg`Название турнира`,
    sample: msg`Футбольная лига Москвы`,
    description: msg`Название организатора, а не турнира: подставляется имя организации, от которой выходит картинка. Название самого турнира — «Название чемпионата».`,
    examples: [msg`Футбольная лига Москвы`, msg`Любительская лига`, msg`ФК Арена`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.TOURNAMENT_LOGO]: {
    label: msg`Логотип турнира`,
    description: msg`Логотип организатора из его профиля. Если логотипа нет, место останется пустым.`,
    output: 'image',
    missingFields: none,
  },
  [PlaceholderKey.MATCH_DATE]: {
    label: msg`Дата матча`,
    sample: msg`12 октября`,
    description: msg`Дата матча. Вид задаётся форматом в инспекторе. Если дата матча не назначена, место останется пустым.`,
    descriptionFor: {
      multiMatch: msg`Дата матча с выбранным номером. Вид задаётся форматом в инспекторе. Если дата не назначена, место останется пустым.`,
    },
    examples: [msg`12 октября`, msg`12.10.2026`, msg`суббота, 12 октября`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.MATCH_TIME]: {
    label: msg`Время матча`,
    sample: msg`19:00`,
    description: msg`Время начала матча. Если время не назначено, место останется пустым.`,
    descriptionFor: {
      multiMatch: msg`Время начала матча с выбранным номером. Если время не назначено, место останется пустым.`,
    },
    examples: [msg`19:00`, msg`11:30`, msg`20:45`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.MATCH_VENUE]: {
    label: msg`Место проведения`,
    sample: msg`Лужники`,
    description: msg`Площадка, на которой играют матч. Если площадка не указана, место останется пустым.`,
    descriptionFor: {
      multiMatch: msg`Площадка матча с выбранным номером. Если площадка не указана, место останется пустым.`,
    },
    examples: [msg`Лужники`, msg`Стадион «Труд»`, msg`Манеж на Ленина`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.ROUND_NAME]: {
    label: msg`Название раунда`,
    sample: msg`1/4 финала`,
    description: msg`Тур или стадия матча. Если у матча раунда нет, место останется пустым.`,
    descriptionFor: {
      multiMatch: msg`Тур или стадия матча с выбранным номером. Если у матча раунда нет, место останется пустым.`,
    },
    examples: [msg`1/4 финала`, msg`5 тур`, msg`Финал`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.PLAYER_NAME]: {
    label: msg`Имя игрока`,
    sample: msg`Иван Петров`,
    description: msg`Имя и фамилия игрока в выбранном в инспекторе формате. В статистике — игрок на этом месте рейтинга.`,
    descriptionFor: {
      playerStats: msg`Имя и фамилия игрока на этом месте рейтинга, в формате из инспектора.`,
    },
    examples: [msg`Иван Петров`, msg`И. Петров`, msg`Петров`],
    output: 'text',
    missingFields: playerMissing,
  },
  [PlaceholderKey.PLAYER_NUMBER]: {
    label: msg`Номер игрока`,
    sample: msg`10`,
    description: msg`Игровой номер из заявки на турнир. Если номера в заявке нет, место останется пустым.`,
    examples: [msg`10`, msg`7`, msg`99`],
    output: 'text',
    missingFields: playerMissing,
  },
  [PlaceholderKey.PLAYER_PHOTO]: {
    label: msg`Фото игрока`,
    description: msg`Фото игрока из его карточки. В статистике вместо отсутствующего фото подставляется логотип его команды.`,
    descriptionFor: {
      playerStats: msg`Фото игрока на этом месте рейтинга. Если фото нет, подставляется логотип его команды.`,
    },
    output: 'image',
    missingFields: playerMissing,
  },
  // Group-level, not row-level: the name comes from the render target itself,
  // so no teamNumber is read. On a non-GROUP stage there is no group at all and
  // the resolver drops the placeholder — that's data availability, not an
  // authoring mistake, so it never reports a missing field.
  [PlaceholderKey.GROUP_NAME]: {
    label: msg`Название группы`,
    sample: msg`Группа A`,
    description: msg`Название группы этапа, для которой рисуется таблица. На этапе без групп место останется пустым.`,
    examples: [msg`Группа A`, msg`Группа Б`, msg`Восток`],
    output: 'text',
    missingFields: none,
  },
  [PlaceholderKey.TOURNAMENT_POSITION]: {
    label: msg`Позиция в турнире`,
    sample: msg`1`,
    description: msg`Место команды в таблице этапа.`,
    examples: [msg`1`, msg`4`, msg`12`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GAMES_PLAYED]: {
    label: msg`Игр сыграно`,
    sample: msg`12`,
    description: msg`Сколько матчей команда сыграла на этапе.`,
    examples: [msg`12`, msg`0`, msg`7`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GAMES_WON]: {
    label: msg`Игр выиграно`,
    sample: msg`8`,
    description: msg`Победы команды на этапе.`,
    examples: [msg`8`, msg`0`, msg`5`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GAMES_LOST]: {
    label: msg`Игр проиграно`,
    sample: msg`2`,
    description: msg`Поражения команды на этапе.`,
    examples: [msg`2`, msg`0`, msg`6`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GAMES_DRAWN]: {
    label: msg`Игр сыграно вничью`,
    sample: msg`2`,
    description: msg`Ничьи команды на этапе.`,
    examples: [msg`2`, msg`0`, msg`3`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GOALS_SCORED]: {
    label: msg`Голов забито`,
    sample: msg`25`,
    description: msg`Сколько голов команда забила на этапе.`,
    examples: [msg`25`, msg`3`, msg`41`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GOALS_CONCEDED]: {
    label: msg`Голов пропущено`,
    sample: msg`11`,
    description: msg`Сколько голов команда пропустила на этапе.`,
    examples: [msg`11`, msg`0`, msg`27`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GOAL_DIFFERENCE]: {
    label: msg`Разница мячей`,
    sample: msg`+14`,
    description: msg`Забитые минус пропущенные голы команды на этапе.`,
    examples: [msg`+14`, msg`0`, msg`-5`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.GAMES_DIFFERENCE]: {
    label: msg`Разница игр (выиграно − проиграно)`,
    sample: msg`+6`,
    description: msg`Победы минус поражения команды на этапе; ничьи не считаются.`,
    examples: [msg`+6`, msg`0`, msg`-3`],
    output: 'text',
    missingFields: teamMissing,
  },
  [PlaceholderKey.POINTS]: {
    label: msg`Очки`,
    sample: msg`26`,
    description: msg`Очки команды в таблице этапа.`,
    examples: [msg`26`, msg`0`, msg`13`],
    output: 'text',
    missingFields: teamMissing,
  },
  // Per-championship player statistics. The `(игрок)` suffix disambiguates them
  // from the team-level standing keys above ('Игр сыграно', 'Голов забито', …),
  // following SCORE_TEAM's 'Счёт (команда)' precedent. The two sets can never
  // appear in the same dropdown via `allowedPlaceholderKeys` — but the inspector
  // falls back to every kind-compatible key while `templateType` is still
  // undefined (placeholder-identity-section.tsx), and in that window both are
  // listed. Labels must therefore be globally unique; a test asserts it.
  [PlaceholderKey.PLAYER_POSITION]: {
    label: msg`Амплуа игрока`,
    sample: msg`Нападающий`,
    description: msg`Амплуа игрока на этом месте рейтинга из его заявки на турнир. Если амплуа не указано, место останется пустым.`,
    examples: [msg`Нападающий`, msg`Защитник`, msg`Вратарь`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_GAMES_PLAYED]: {
    label: msg`Игр сыграно (игрок)`,
    sample: msg`12`,
    description: msg`Сколько матчей турнира сыграл игрок на этом месте рейтинга.`,
    examples: [msg`12`, msg`3`, msg`20`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_GOALS]: {
    label: msg`Голов забито (игрок)`,
    sample: msg`9`,
    description: msg`Голы игрока на этом месте рейтинга за турнир.`,
    examples: [msg`9`, msg`0`, msg`17`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_ASSISTS]: {
    label: msg`Голевых передач (игрок)`,
    sample: msg`5`,
    description: msg`Голевые передачи игрока на этом месте рейтинга за турнир.`,
    examples: [msg`5`, msg`0`, msg`11`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_GOALS_ASSISTS]: {
    label: msg`Голы + передачи (игрок)`,
    sample: msg`14`,
    description: msg`Сумма голов и голевых передач игрока на этом месте рейтинга за турнир.`,
    examples: [msg`14`, msg`2`, msg`25`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_YELLOW_CARDS]: {
    label: msg`Жёлтых карточек (игрок)`,
    sample: msg`3`,
    description: msg`Жёлтые карточки игрока на этом месте рейтинга за турнир.`,
    examples: [msg`3`, msg`0`, msg`6`],
    output: 'text',
    missingFields: rankMissing,
  },
  [PlaceholderKey.PLAYER_RED_CARDS]: {
    label: msg`Красных карточек (игрок)`,
    sample: msg`1`,
    description: msg`Красные карточки игрока на этом месте рейтинга за турнир.`,
    examples: [msg`1`, msg`0`, msg`2`],
    output: 'text',
    missingFields: rankMissing,
  },
  // One placeholder per team: own goals are listed under the team they were
  // credited to, so the team axis is mandatory. Not in MATCH_SCOPED_KEYS — the
  // key is allowed on the single-match RESULTS template only.
  [PlaceholderKey.GOAL_SCORERS]: {
    label: msg`Авторы голов`,
    sample: msg`Петров 12', 67'`,
    description: msg`Список авторов голов одной команды с минутами. «П» — гол с пенальти, «АГ» — автогол, он стоит в списке команды, которой засчитан; удары серии пенальти — отдельным блоком «Пен.».`,
    examples: [msg`Петров 12', 67'`, msg`П Сидоров 45'`, msg`АГ Иванов 30'`],
    output: 'text',
    missingFields: teamMissing,
  },
} satisfies Record<PlaceholderKey, RegistryEntry>;

export function getRegistryEntry(key: string): RegistryEntry | undefined {
  return (PLACEHOLDER_KEY_REGISTRY as Record<string, RegistryEntry>)[key];
}

/**
 * The key's description for the active template type — its type-specific
 * variant when there is one, otherwise the general text (also while the type is
 * still loading).
 */
export function getKeyDescription(key: string, templateType?: TemplateType): MessageDescriptor | undefined {
  const entry = getRegistryEntry(key);
  if (!entry) return undefined;
  return (templateType && entry.descriptionFor?.[DESCRIPTION_SCOPES[templateType]]) || entry.description;
}

const expectedOutputForKind =(kind: PlaceholderKind | undefined): 'text' | 'image' | undefined => {
  if (kind === 'placeholder-image') return 'image';
  if (kind === 'placeholder-text') return 'text';
  return undefined;
};

export function isKeyCompatibleWithKind(key: string, kind: PlaceholderKind | undefined): boolean {
  const entry = getRegistryEntry(key);
  const expected = expectedOutputForKind(kind);
  if (!entry || !expected) return false;
  return entry.output === expected;
}

export function getKeysForKind(kind: PlaceholderKind): readonly PlaceholderKey[] {
  const expected = expectedOutputForKind(kind);
  if (!expected) return [];
  return (Object.values(PlaceholderKey) as PlaceholderKey[]).filter((k) => PLACEHOLDER_KEY_REGISTRY[k].output === expected);
}

/**
 * Keys an author may pick for a placeholder of `kind` on a template of
 * `templateType` — the key picker and the inspector's «Ключ» list share it.
 * Unknown template type (still loading) falls back to every kind-compatible key
 * so the list isn't empty while data loads.
 */
export function getPickableKeys(kind: PlaceholderKind, templateType?: TemplateType): readonly PlaceholderKey[] {
  const allowed = templateType ? getTemplateTypeEntry(templateType).allowedPlaceholderKeys : undefined;
  return getKeysForKind(kind).filter((key) => !allowed || allowed.has(key));
}

const EMPTY_IDENTITY = { teamNumber: undefined, playerNumber: undefined } as PlaceholderData;

/**
 * Which of the team#/player# identity fields a key actually reads on the active
 * template type. Reuses each entry's `missingFields` predicate against an
 * all-empty identity — whatever it reports missing is exactly what it checks,
 * with no separate "which fields does this key use" table to keep in sync.
 *
 * `templateType` is required (unlike `missingFieldsFor`'s optional one): the
 * answer genuinely differs per type — TEAM_NAME reads `teamNumber` on a match
 * template and `playerNumber` on a player-statistics one — so a caller that
 * omitted it would silently render the wrong inspector fields.
 *
 * Unknown/unset keys report every field the type has an axis for — the
 * inspector can't yet tell what the author is about to pick, so it shows
 * everything rather than guessing. A player-statistics template has no team
 * axis, so it never offers `teamNumber` even in that fallback.
 */
export function usedIdentityFieldsFor(
  key: string | undefined,
  templateType: TemplateType | undefined,
): ReadonlySet<'teamNumber' | 'playerNumber'> {
  const entry = key ? getRegistryEntry(key) : undefined;
  if (!entry) {
    return isPlayerStats(templateType)
      ? new Set<'teamNumber' | 'playerNumber'>(['playerNumber'])
      : new Set<'teamNumber' | 'playerNumber'>(['teamNumber', 'playerNumber']);
  }
  const fields = entry.missingFields(EMPTY_IDENTITY, templateType);
  return new Set(fields.filter((f): f is 'teamNumber' | 'playerNumber' => f === 'teamNumber' || f === 'playerNumber'));
}

export type PickIdentityField = 'teamNumber' | 'playerNumber' | 'matchNumber';

/**
 * Which slot numbers the key picker asks for when a placeholder of `key` is
 * created on a template of `templateType`, in display order. Built on
 * `usedIdentityFieldsFor` and `needsMatchNumber`, so it cannot drift from what
 * the inspector checks. Unknown template type (still loading) asks for nothing —
 * the author sets the numbers in the inspector, as before.
 */
export function pickIdentityFields(key: string, templateType?: TemplateType): readonly PickIdentityField[] {
  if (!templateType || !getRegistryEntry(key)) return [];
  const used = usedIdentityFieldsFor(key, templateType);
  const out: PickIdentityField[] = [];
  if (used.has('teamNumber')) out.push('teamNumber');
  if (used.has('playerNumber')) out.push('playerNumber');
  if (needsMatchNumber(key, templateType)) out.push('matchNumber');
  return out;
}

export function missingFieldsFor(
  data: PlaceholderData,
  templateType?: TemplateType,
): readonly string[] {
  const entry = getRegistryEntry(data.placeholderKey);
  if (!entry) return ['placeholderKey'];
  const expected = expectedOutputForKind(data.kind);
  if (expected && entry.output !== expected) return ['placeholderKey'];
  const base = entry.missingFields(data, templateType);
  if (needsMatchNumber(data.placeholderKey, templateType) && !data.matchNumber) {
    return [...base, 'matchNumber'];
  }
  return base;
}
